import React from 'react';

import { classnames, useClassnames } from '@lumapps/classnames';
import { useDndContext } from '@lumapps/lumx-dnd/hooks';
import { TableBody } from '@lumapps/lumx/react';

import { getNewSortableListIndexFromKeyboardEvent } from '../../../utils/getNewSortableListIndexFromKeyboardEvent';
import { SortableListProps } from '../types';
import './index.scss';

const CLASSNAME = 'lumx-sortable-list-container';

export interface SortableListContainerProps<T>
    extends Pick<SortableListProps<T>, 'columns' | 'gap' | 'className'>,
        Pick<Required<SortableListProps<T>>, 'mode'> {
    containerRef?: React.RefObject<HTMLOListElement & HTMLTableSectionElement>;
    itemRefs?: React.MutableRefObject<(HTMLLIElement | HTMLTableRowElement | null)[]>;
}

export const SortableListContainer = <T,>({
    mode,
    columns,
    className,
    gap,
    containerRef,
    itemRefs,
    children,
}: React.PropsWithChildren<SortableListContainerProps<T>>) => {
    const { block } = useClassnames(CLASSNAME);
    const { active } = useDndContext();

    const handleKeyDown = (event: React.KeyboardEvent) => {
        const allItems = itemRefs?.current.filter((ref): ref is HTMLLIElement => ref !== null) || [];

        const newIndex = getNewSortableListIndexFromKeyboardEvent({
            active,
            mode,
            columns,
            allItems,
            event,
        });

        if (newIndex) {
            event.preventDefault();
            event.stopPropagation();
            allItems[newIndex]?.focus();
        }
    };

    const ContainerElement = mode === 'table' ? TableBody : 'ol';

    return (
        <ContainerElement
            ref={containerRef}
            className={classnames(
                block({
                    [mode]: true,
                    'fixed-columns': Boolean(columns),
                }),
                className,
            )}
            style={
                {
                    '--columns': columns,
                    // eslint-disable-next-line lumapps/no-ds-classes
                    ...(gap && mode !== 'table' ? { gap: `var(--lumx-spacing-unit-${gap})` } : {}),
                    // eslint-disable-next-line lumapps/no-ds-classes
                    ...(gap && mode === 'table' ? { 'border-spacing': `var(--lumx-spacing-unit-${gap}) 0` } : {}),
                } as React.CSSProperties
            }
            onKeyDown={handleKeyDown}
        >
            {children}
        </ContainerElement>
    );
};
